// @flow
import React from 'react';
import { useDispatch } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import * as moment from 'moment';
import axios from 'axios';
import QRCode from 'qrcode.react';
import Button from '@material-ui/core/Button';
import classes from "../invoice/Invoice.module.css";
import {
	MACHINE_ORDER,
} from '../../constants';
import { showErrorMsg } from '../../actions/snackbar';

const useStyles = makeStyles((theme) => ({
	qrCodeText: {
		color: '#f05b83',
		fontSize: 14,
		textAlign: 'center',
		padding: '0 20px',
		'& div:nth-child(2)': {
			fontSize: 16,
			fontWeight: 600,
		},
		'& a:last-child': {
			cursor: 'pointer',
			textDecoration: 'underline',
			color: 'inherit',
		},
	},
	accessCode: {
		fontSize: 14,
		fontWeight: 'bold',
		textAlign: 'center',
		margin: '0 0 14px',
		color: '#3e3e3e',
		lineHeight: '18px',
		'& > section:first-child': {
			marginBottom: 4,
		},
	},
}));

type Props = {
	orderDetails: any,
	isOrderActive: Function,
	selectedPod: any,
	accessCode: string,
	bQRViewer: boolean,
	bServer_Order: boolean,
	bVending_Order: boolean,
	openDoorProcessing: boolean,
	setOpenDoorProcessing: Function,
	handleSelectOrder: Function,
	history: any,
};

function MachinePickupSection({
	orderDetails,
	isOrderActive,
	selectedPod,
	accessCode,
	bQRViewer,
	bServer_Order,
	bVending_Order,
	openDoorProcessing,
	setOpenDoorProcessing,
	handleSelectOrder,
	history,
}: Props) {
	const styles = useStyles();
	const dispatch = useDispatch();

	// Debug log to show what props are received
	console.log('🔧 MachinePickupSection props received:', {
		orderId: orderDetails?.order_id,
		hasHistory: !!history,
		hasHandleSelectOrder: !!handleSelectOrder,
		openDoorProcessing,
		hasSetOpenDoorProcessing: !!setOpenDoorProcessing,
		parentComponent: history ? 'likely PaymentSuccess' : 'likely InvoicePage'
	});

	const handleOpenDoor = async () => {
		console.log('🚪 MachinePickupSection handleOpenDoor called:', {
			orderId: orderDetails.order_id,
			hasHistory: !!history,
			historyType: typeof history,
			historyKeys: history ? Object.keys(history) : 'no history'
		});

		setOpenDoorProcessing(true);

		try {
			// Call the process order status URL BEFORE navigation
			const processOrderUrl = `https://neuustore.com/tm/process-order/status/${orderDetails.order_id}?__md=1`;
			console.log('📞 Calling process order URL:', processOrderUrl);

			const response = await axios.get(processOrderUrl);
			console.log('✅ Process order URL called successfully:', response.data);

			// Check if another order is in progress
			if (response.data?.response?.status === "Open Door") {
				console.log('⚠️ Another order is in progress');

				// Show error popup using snackbar
				dispatch(showErrorMsg("Another order in progress. Please try again later.", 4000));

				// Re-enable button so user can try again
				setOpenDoorProcessing(false);
				return;
			}

			// Set order in Redux for instant display
			handleSelectOrder(orderDetails);

			// Navigate to process order page
			const targetUrl = `/orders/${orderDetails.order_id}/process`;
			console.log('🧭 Attempting navigation to:', targetUrl);

			if (!history) {
				console.error('🔴 No history object provided to MachinePickupSection!');
				setOpenDoorProcessing(false);
				return;
			}

			if (!history.push) {
				console.error('🔴 History object missing push method!', history);
				setOpenDoorProcessing(false);
				return;
			}

			console.log('🎯 Calling history.push with:', targetUrl);
			history.push(targetUrl);
			console.log('✅ Navigation completed');
		} catch (error) {
			console.error('🔴 Error calling process order URL:', error);

			// Show error message for network/API errors
			dispatch(showErrorMsg("Failed to process order. Please try again.", 3000));

			// Re-enable button so user can try again
			setOpenDoorProcessing(false);
		}
	};

	// Only render for machine orders that are active
	if (orderDetails.type_of_order !== MACHINE_ORDER || !isOrderActive(orderDetails)) {
		return null;
	}

	return (
		<section>
			{/* Regular Machine Orders (not TM08202103) */}
			{orderDetails.vm_imei_id !== 'TM08202103' && (
				<React.Fragment>
					{/* QR Code Display */}
					{bQRViewer && (
						<section className={styles.qrCodeText} id='qrcode'>
							<section style={{ alignSelf: 'center', marginBottom: 6 }}>
								Show this QR code on the machine tab (touchless experience)
							</section>
							<QRCode value={accessCode} size={250} />
							<div style={{ margintop: 4 }}>OR</div>
						</section>
					)}

					{/* Open Door Button Section */}
					{bServer_Order && (
						<section className={classes.qrCodeText} id='qrcode'>
							<section style={{ alignSelf: 'center', marginBottom: 6 }}>
								Please get near the machine and click open Door
							</section>

							<section>
								<p style={{font: 'bold',lineHeight:'24px',fontSize: '24px',}}>
									Machine Name: <br/> {selectedPod.location}
								</p>
								<Button
									variant='contained'
									aria-label='Support'
									style={{
										background: openDoorProcessing ? '#ccc' : '#bfd730',
										font: 'bold',
										textDecoration: 'none',
										lineHeight:'42px',
										marginBottom: '10px',
										boxShadow: '0px 1px 5px 0px',
										width: '250px',
										minWidth: '250px',
										color: openDoorProcessing ? '#666' : 'black',
										border_radius: '5px',
										cursor: openDoorProcessing ? 'not-allowed' : 'pointer'
									}}
									onClick={handleOpenDoor}
									disabled={openDoorProcessing}
								>
									{openDoorProcessing ? "Processing..." : (
										bVending_Order ? "Tap To Vend" : "Open Door"
									)}
								</Button>
							</section>
						</section>
					)}

					{/* Access Code with Server Order */}
					{bServer_Order && (
						<section className={styles.accessCode}>
							<div style={{ color: '#f05b83' }}>
								Enter access code: <b>{orderDetails.access_code}</b>
							</div>
							<div>
								(EXPIRES AT
								{moment(orderDetails.order_modified_at || orderDetails.last_modified_at)
									.add(1, 'h')
									.format(' h:mm a')}
								)
							</div>
						</section>
					)}

					{/* Access Code without Server Order (excluding specific machine IDs) */}
					{!(orderDetails.vm_imei_id === 'TM12202102' || orderDetails.vm_imei_id === 'TM12202103') && !bServer_Order && (
						<section className={styles.accessCode}>
							<div style={{ color: '#f05b83' }}>
								Enter access code: <b>{orderDetails.access_code}</b>
							</div>
							<div>
								(EXPIRES AT
								{moment(orderDetails.order_modified_at || orderDetails.last_modified_at)
									.add(1, 'h')
									.format(' h:mm a')}
								)
							</div>
						</section>
					)}
				</React.Fragment>
			)}

			{/* Special TM08202103 Machine - Cafe Person */}
			{orderDetails.vm_imei_id === 'TM08202103' && (
				<React.Fragment>
					<section className={styles.accessCode}>
						<div style={{ color: '#f05b83' }}>
							Transaction Code:: <b>{orderDetails.access_code}</b>
						</div>
						<div></div>
						<div>
							<br/>Please show this to the Cafe Person
							<br/>and collect your item
						</div>
					</section>
				</React.Fragment>
			)}
		</section>
	);
}

export default MachinePickupSection;
